<template>
  <div class="kjp-tiles d-flex px-3 pt-3">
    <div v-for="(tile, i) in tiles" :key="tile.scope" class="kjp-tile" :class="[{ 'ml-3': i > 0 }, `kjp-tile-${tile.state}`]">
      <div class="kjp-tile-name">{{ label(tile.scope) }}</div>

      <template v-if="tile.state === 'ok' || tile.state === 'over'">
        <div class="kjp-tile-row">
          <span class="kjp-tile-key">Allocated</span>
          <span class="kjp-tile-val">{{ fmt(tile.allocated) }} {{ tile.unit }}</span>
        </div>
        <div class="kjp-tile-row">
          <span class="kjp-tile-key">Used</span>
          <span class="kjp-tile-val">{{ fmt(tile.used) }} <span class="kjp-tile-pct">({{ pct(tile.usedPct) }})</span></span>
        </div>

        <!-- A single ratio against a limit is a meter, not a chart. The track
             is the allocation, the fill is what has been used. -->
        <div class="kjp-meter" role="img" :aria-label="`${pct(tile.usedPct)} of allocation used`">
          <div class="kjp-meter-fill" :style="{ width: meterWidth(tile) }"></div>
        </div>

        <div class="kjp-tile-row kjp-tile-remaining">
          <span class="kjp-tile-key">Remaining</span>
          <span class="kjp-tile-val">
            {{ fmt(tile.remaining) }} <span class="kjp-tile-pct">({{ pct(tile.remainingPct) }})</span>
          </span>
        </div>

        <!-- Status colour never travels alone: over-ceiling is named in words
             and carries an icon, so it does not depend on the red being seen. -->
        <div v-if="tile.state === 'over'" class="kjp-tile-flag">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" /> Over annual ceiling
        </div>
      </template>

      <!-- Unmeasured: the allocation is known (it comes from contract road
           lengths, not from cases), so it is shown. Only the figures that
           genuinely depend on missing measurements read N/A. -->
      <template v-else-if="tile.state === 'unmeasured'">
        <div class="kjp-tile-row">
          <span class="kjp-tile-key">Allocated</span>
          <span class="kjp-tile-val">{{ fmt(tile.allocated) }} {{ tile.unit }}</span>
        </div>
        <div class="kjp-tile-row">
          <span class="kjp-tile-key">Used</span>
          <span class="kjp-tile-val kjp-tile-na">N/A</span>
        </div>

        <!-- Same slot as the measured tiles: directly under Used, above the
             Remaining divider. The meter still renders, but uniformly grey and
             unfilled rather than as a blue bar at 0% — an empty track alone
             would read as "0% used", a flat grey one reads as "no reading",
             which is what the N/A values already say. -->
        <div class="kjp-meter kjp-meter-na" role="img" aria-label="Usage not recorded"></div>

        <div class="kjp-tile-row kjp-tile-remaining">
          <span class="kjp-tile-key">Remaining</span>
          <span class="kjp-tile-val kjp-tile-na">N/A</span>
        </div>

        <div class="kjp-tile-note">No dimensions recorded on any case</div>
      </template>

      <div v-else class="kjp-tile-blank">{{ blankMessage(tile.state) }}</div>
    </div>
  </div>
</template>

<script>
import { round2 } from "@/kjp";
import { scopeLabel } from "@/components/kjp/scopes";

// Why each non-numeric state says something different: a reader who sees a
// blank tile needs to know whether the figure is zero, unknown, or not yet
// measured. Collapsing these into one message (or worse, into "0.00") is the
// same fabrication src/kjp.js's `unmeasured` flag exists to prevent.
// `unmeasured` is absent here on purpose: that state now renders its own
// block (allocation plus N/A), because the allowance is known even when the
// usage is not. Only states with nothing at all to show fall through to here.
const BLANK_MESSAGES = {
  empty: "No data for the selected districts and year",
  failed: "Could not load — click Apply to try again",
};

export default {
  name: "kjp-tiles",
  props: {
    tiles: { type: Array, required: true },
  },
  methods: {
    label(scope) {
      return scopeLabel(scope);
    },
    fmt(v) {
      return round2(v).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    pct(v) {
      return `${round2(v).toFixed(1)}%`;
    },
    // Clamped at 100% so an over-ceiling scope fills the track rather than
    // overflowing the card; the "Over annual ceiling" flag carries the overrun,
    // which a bar running past its own track could not show anyway.
    meterWidth(tile) {
      return `${Math.max(0, Math.min(100, tile.usedPct))}%`;
    },
    blankMessage(state) {
      return BLANK_MESSAGES[state] || BLANK_MESSAGES.empty;
    },
  },
};
</script>

<style lang="scss" scoped>
// Palette kept in step with SCREEN_COLORS in chartScreen.js — the tiles sit
// directly above the chart, so a different blue for "used" in each would read
// as two different quantities.
$tile-surface: #1b1b1b;
$actual: #3987e5;
$critical: #d03b3b;
$text-primary: #ffffff;
$text-secondary: #c3c2b7;
$text-muted: #8b8a84;

.kjp-tiles {
  flex: 0 0 auto;
}

.kjp-tile {
  flex: 1 1 0;
  min-width: 0;
  background-color: $tile-surface;
  border-radius: 4px;
  // The accent edge fig1 uses. Colour alone never carries the state — the
  // blank message and the over-ceiling flag say it in words too.
  border-left: 3px solid $actual;
  padding: 0.65rem 0.85rem 0.7rem;
}

.kjp-tile-over {
  border-left-color: $critical;
}

.kjp-tile-unmeasured,
.kjp-tile-empty,
.kjp-tile-failed {
  border-left-color: #4a4a4a;
}

.kjp-tile-name {
  color: $text-primary;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
}

.kjp-tile-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.75rem;
  line-height: 1.5;
}

.kjp-tile-key {
  color: $text-muted;
  padding-right: 0.5rem;
}

// Figures stay in text ink rather than wearing the series colour — the meter
// beside them carries the identity. Tabular numerals so the three tiles'
// digits line up vertically instead of shimmering against each other.
.kjp-tile-val {
  color: $text-secondary;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.kjp-tile-pct {
  color: $text-muted;
}

.kjp-tile-remaining {
  border-top: 1px solid #2e2e2e;
  margin-top: 0.4rem;
  padding-top: 0.4rem;

  .kjp-tile-key,
  .kjp-tile-val {
    color: $text-primary;
    font-weight: 600;
  }

  .kjp-tile-pct {
    font-weight: 400;
    color: $text-secondary;
  }
}

.kjp-meter {
  height: 4px;
  border-radius: 2px;
  background-color: #2e2e2e;
  margin-top: 0.45rem;
  overflow: hidden;
}

.kjp-meter-fill {
  height: 100%;
  background-color: $actual;
  border-radius: 2px;
}

.kjp-tile-over .kjp-meter-fill {
  background-color: $critical;
}

// Unmeasured: a flat grey bar, no fill of its own. Distinct from both the
// empty track (which would read as 0% used) and the blue fill (a real
// reading) — it is the same shape as its neighbours, in the colour the tile
// uses for "not known".
.kjp-meter-na {
  background-color: #3a3a3a;
}

.kjp-tile-flag {
  color: $critical;
  font-size: 0.7rem;
  margin-top: 0.35rem;
}

.kjp-tile-blank {
  color: $text-muted;
  font-size: 0.72rem;
  font-style: italic;
  line-height: 1.4;
  padding: 0.3rem 0 0.55rem;
}

// N/A is deliberately dimmer than a real figure, so a glance down the row of
// tiles separates "known" from "not recorded" without having to read.
.kjp-tile-na {
  color: $text-muted;
  font-weight: 400 !important;
}

.kjp-tile-note {
  color: $text-muted;
  font-size: 0.68rem;
  font-style: italic;
  margin-top: 0.35rem;
}
</style>
